/**
 * Pure decision logic for a single CRF form's download controls.
 *
 * Extracted out of CRFBuilder.tsx so the "which download options does this form
 * show" rules are unit-testable without React (web has no test runner; these
 * are exercised via the functions package's vitest). The shape below is a
 * structural subset of CRFBuilder's CRFForm.
 *
 * Source-path semantics (the reason "Source PDF" was overloaded):
 *   - `sourcePacketPath` is set ONLY by the packet extractor, so it cleanly
 *     marks a form as *uploaded* (vs generated). For an uploaded form it points
 *     at the original uploaded document.
 *   - `sourceFilePath` means different things by form kind: for an uploaded
 *     form it's a per-form clip of the original upload; for a generated form
 *     it's the rendered *blank-CRF PDF* that renderAndPersistCrf stores.
 */
export interface DownloadForm {
  status: string;
  variables: unknown[] | null;
  sourceFilePath?: string;
  sourcePacketPath?: string;
}

/** True when the form was imported from an uploaded CRF packet (vs generated). */
export function isUploadedForm(form: DownloadForm): boolean {
  return Boolean(form.sourcePacketPath);
}

/**
 * True when the form has a rendering the CRF Instruction Guide can embed.
 * The guide rasterizes PDF pages only, so either path counts only when it
 * names a PDF: `sourceFilePath` (per-form clip, platform render, or a file
 * attached with "Attach rendering") and `sourcePacketPath` (when the uploaded
 * packet itself is a PDF). A Word (.doc/.docx) upload gives the platform
 * structured content but no embeddable page image — those forms need Attach
 * rendering before the guide can include them. Mirrors
 * findMissingCrfRenderings in functions/src/crfGuide/, and
 * attachRendering.test.ts pins the two to the same answers.
 */
export function hasUsableRendering(
  form: Pick<DownloadForm, 'sourceFilePath' | 'sourcePacketPath'>
): boolean {
  const isPdf = (p?: string) => typeof p === 'string' && /\.pdf$/i.test(p);
  return isPdf(form.sourceFilePath) || isPdf(form.sourcePacketPath);
}

export type BlankPdfDownload = 'available' | 'pending' | 'hidden';

/**
 * State of the "Download PDF" (rendered blank CRF) option for a form:
 *   - 'available' — a rendered blank-CRF PDF exists (generated form with
 *     sourceFilePath); offer the download.
 *   - 'pending'   — generated, approved/final, but not rendered yet; show the
 *     option disabled with a hint (we do NOT build an on-demand single-form
 *     PDF render in this pass).
 *   - 'hidden'    — not applicable: the form isn't approved/final or has no
 *     variables, OR it's an uploaded form (whose sourceFilePath is the original
 *     upload, surfaced via the "Original upload" control instead).
 */
export function blankPdfDownloadState(form: DownloadForm): BlankPdfDownload {
  const approvedWithVars =
    (form.status === 'approved' || form.status === 'final') &&
    Array.isArray(form.variables) &&
    form.variables.length > 0;
  if (!approvedWithVars) return 'hidden';
  if (isUploadedForm(form)) return 'hidden';
  return form.sourceFilePath ? 'available' : 'pending';
}
